import { LoadingButton } from '@mui/lab'
import {
    Alert,
    Autocomplete,
    Box,
    CircularProgress,
    FormControlLabel,
    Paper,
    Stack,
    Switch,
    TextField,
    Typography,
} from '@mui/material'
import DashboardLayout from '@pages/Dashboard/components/DashboardLayout'
import { useEffect, useMemo, useRef, useState } from 'react'
import CallMergeIcon from '@mui/icons-material/CallMerge'
import { z } from 'zod'
import { enqueueSnackbar, SnackbarProvider } from 'notistack'
import API from '@enums/api'
import { useDebouncedSearch } from '@hooks/useDebounceSearch'
import MergePreview from './MergePreview'
import { MergePlan, MergePlanSchema, MergeResultSchema } from './merge-schemas'

/**
 * Both pickers search Habitat Accounts per keystroke instead of loading the
 * whole user table up front.
 *
 * This page used to load the whole user table in one go — every account on the
 * platform, with no limit and no `deleted_at` filter. At 61k rows that response
 * alone was several MB, and MUI's Autocomplete renders one `<li>` per option
 * with no virtualisation, so opening either dropdown pinned the main thread and
 * the page stopped responding to clicks and typing entirely.
 */
const MIN_QUERY_LENGTH = 2
const SEARCH_LIMIT = 50

const ZodUser = z.object({
    id: z.string().uuid(),
    first_name: z.string().nullable(),
    last_name: z.string().nullable(),
    email: z.string(),
    deleted_at: z.string().nullable().optional(),
})

const expectedUserSearchResponse = z.array(ZodUser)

type AutocompleteOption = {
    id: string
    label: string
    email: string
    deleted: boolean
}

/**
 * Habitat Accounts allows either name column to be null, so fall back to the
 * email rather than rendering an option labelled " " that nothing can match.
 */
function toOption(user: z.infer<typeof ZodUser>): AutocompleteOption {
    const name = [user.first_name, user.last_name]
        .filter((part) => part && part.trim().length > 0)
        .join(' ')

    return {
        id: user.id,
        label: name.length > 0 ? name : user.email,
        email: user.email,
        deleted: Boolean(user.deleted_at),
    }
}

async function searchUsers(query: string): Promise<AutocompleteOption[]> {
    const res = await API.searches.queryUsers(query, SEARCH_LIMIT)

    if (!res.ok) {
        throw new Error('Failed to search users.')
    }

    const { data, error } = expectedUserSearchResponse.safeParse(res.value)

    if (error) {
        console.error(error)
        throw new Error('Unexpected response from the user search.')
    }

    return data.map(toOption)
}

/**
 * The button says what pressing it will do. A toggle that only changes what
 * happens after the click is too easy to lose track of.
 */
function mergeButtonLabel(blocked: boolean, dryRun: boolean): string {
    if (blocked) {
        return 'Merge blocked'
    }
    if (dryRun) {
        return 'Test the move'
    }
    return 'Merge'
}

export default function MergeAccountsPage() {
    const [waiting, setWaiting] = useState<boolean>(false)

    const [source, setSource] = useState<AutocompleteOption | null>(null)
    const [destination, setDestination] = useState<AutocompleteOption | null>(
        null
    )

    const [dryRun, setDryRun] = useState<boolean>(false)
    const [plan, setPlan] = useState<MergePlan | null>(null)
    const [planLoading, setPlanLoading] = useState<boolean>(false)
    const [planError, setPlanError] = useState<string | null>(null)

    const [creatingDestinationRecord, setCreatingDestinationRecord] =
        useState<boolean>(false)
    const [cancellingOrders, setCancellingOrders] = useState<boolean>(false)
    // Bumped to ask for a fresh plan without touching the selection, after
    // something has changed underneath it.
    const [planReloads, setPlanReloads] = useState<number>(0)

    // Preview requests race each other when the destination is changed quickly,
    // so only the newest one is allowed to write to state.
    const latestPreview = useRef(0)

    const reset = () => {
        setSource(null)
        setDestination(null)
        setPlan(null)
        setPlanError(null)
    }

    useEffect(() => {
        if (!source || !destination) {
            // Bump the counter so a preview still in flight for the previous
            // pair cannot land after the selection has been cleared.
            latestPreview.current += 1
            setPlan(null)
            setPlanError(null)
            setPlanLoading(false)
            return
        }

        const requestId = latestPreview.current + 1
        latestPreview.current = requestId

        const loadPreview = async () => {
            setPlanLoading(true)
            setPlanError(null)

            const res = await API.actions.previewMergeAccounts(
                source.id,
                destination.id
            )

            if (requestId !== latestPreview.current) {
                return
            }

            setPlanLoading(false)

            if (!res.ok) {
                setPlan(null)
                setPlanError('Could not work out what this merge would do.')
                return
            }

            const { data, error } = MergePlanSchema.safeParse(res.value)

            if (error) {
                console.error(error)
                setPlan(null)
                setPlanError('Unexpected response from the merge preview.')
                return
            }

            setPlan(data)
        }

        loadPreview()
    }, [source, destination, planReloads])

    const blocked = (plan?.blockers.length ?? 0) > 0
    const canMerge = Boolean(plan) && !blocked && !planLoading && !waiting

    const dryRunRequested = dryRun

    /**
     * Give the destination the Messenger Pigeon row it is missing, then ask
     * for the plan again so the blocker disappears without the selection being
     * cleared and re-picked.
     */
    const createDestinationRecord = async () => {
        if (!destination || creatingDestinationRecord) {
            return
        }

        setCreatingDestinationRecord(true)

        const res = await API.actions.createMessengerPigeonUser(destination.id)

        setCreatingDestinationRecord(false)

        if (!res.ok) {
            enqueueSnackbar({
                message: 'Could not create the Messenger Pigeon record.',
                variant: 'error',
            })
            return
        }

        enqueueSnackbar({
            message: 'Messenger Pigeon record created.',
            variant: 'success',
        })

        setPlanReloads((reloads) => reloads + 1)
    }

    /**
     * Close off the source's abandoned checkouts, then ask for the plan again.
     *
     * The ids come from the rows the preview drew, so a click only ever acts
     * on orders somebody has actually looked at. The server checks the status
     * again before writing, so one that has moved on since is left alone and
     * comes back in `skipped`.
     */
    const cancelPendingPaymentOrders = async (orderIds: string[]) => {
        if (!source || cancellingOrders || orderIds.length === 0) {
            return
        }

        setCancellingOrders(true)

        const res = await API.actions.cancelPendingPaymentOrders(
            source.id,
            orderIds
        )

        setCancellingOrders(false)

        if (!res.ok) {
            enqueueSnackbar({
                message: 'Could not cancel those orders.',
                variant: 'error',
            })
            return
        }

        enqueueSnackbar({
            message: 'Unpaid orders cancelled.',
            variant: 'success',
        })

        setPlanReloads((reloads) => reloads + 1)
    }

    const submit = async () => {
        if (!source || !destination || !canMerge) {
            return
        }

        // Set only once the guards above have passed. Returning early after
        // flipping this left both pickers disabled with no way back.
        setWaiting(true)
        enqueueSnackbar({
            message: dryRunRequested
                ? 'Testing the move, nothing will be kept...'
                : 'Merging accounts...',
            variant: 'info',
        })

        const res = await API.actions.mergeAccounts(
            source.id,
            destination.id,
            dryRunRequested
        )

        if (!res.ok) {
            enqueueSnackbar({
                message:
                    typeof res.message === 'string'
                        ? res.message
                        : 'Failed to merge accounts.',
                variant: 'error',
            })
            setWaiting(false)
            return
        }

        const { data, error } = MergeResultSchema.safeParse(res.value)

        if (error) {
            console.error(error)
            enqueueSnackbar({
                message: 'Merge ran but the response could not be read.',
                variant: 'warning',
            })
            setWaiting(false)
            return
        }

        const movedRows = data.moved.reduce(
            (running, entry) => running + entry.rows,
            0
        )

        if (data.dryRun) {
            enqueueSnackbar({
                message: `Dry run: would have moved ${movedRows} item(s) to ${destination.email} and disabled ${source.email}. Nothing was changed.`,
                variant: 'info',
                persist: true,
            })
        } else if (!data.sourceDisabled) {
            enqueueSnackbar({
                message: `Moved ${movedRows} item(s) to ${destination.email}, but the old account could not be disabled. Disable ${source.email} by hand, and tell a developer.`,
                variant: 'warning',
                persist: true,
            })
        } else if (movedRows === 0) {
            enqueueSnackbar({
                message: 'Nothing to move. The old account was disabled.',
                variant: 'success',
            })
        } else {
            enqueueSnackbar({
                message: `Moved ${movedRows} item(s) to ${destination.email}. The old account was disabled.`,
                variant: 'success',
            })
        }

        console.log(`Reassigned ${source.id} to ${destination.id}`)

        // Clearing the pickers after a dry run would throw away the pair you
        // were testing. Nothing moved, so there is nothing to clear.
        if (!data.dryRun) {
            reset()
        }

        setWaiting(false)
    }

    return (
        <DashboardLayout loading={waiting}>
            <Box
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    paddingTop: 64,
                }}
            >
                <Paper
                    style={{
                        textAlign: 'center',
                        padding: '2em',
                    }}
                >
                    <Stack direction="column" gap={'1em'}>
                        <Stack direction="row" gap={'5%'}>
                            <Typography variant="body1" sx={{ my: 'auto' }}>
                                Source:
                            </Typography>
                            <UserSelect
                                label={'Source User'}
                                value={source}
                                onChange={setSource}
                                disabled={waiting}
                            />
                        </Stack>
                        {source && (
                            <Stack direction="row" gap={'5%'}>
                                <Typography variant="body1" sx={{ my: 'auto' }}>
                                    Destination:
                                </Typography>
                                <UserSelect
                                    label={'Destination User'}
                                    value={destination}
                                    onChange={setDestination}
                                    disabled={waiting}
                                />
                            </Stack>
                        )}
                        {planLoading && (
                            <Stack
                                direction="row"
                                gap="0.5em"
                                justifyContent="center"
                            >
                                <CircularProgress size={20} />
                                <Typography variant="body2">
                                    Working out what would move...
                                </Typography>
                            </Stack>
                        )}

                        {planError && (
                            <Alert severity="warning">{planError}</Alert>
                        )}

                        {plan && !planLoading && (
                            <MergePreview
                                plan={plan}
                                onCreateDestinationRecord={
                                    createDestinationRecord
                                }
                                creatingDestinationRecord={
                                    creatingDestinationRecord
                                }
                                onCancelPendingPaymentOrders={
                                    cancelPendingPaymentOrders
                                }
                                cancellingOrders={cancellingOrders}
                            />
                        )}

                        {source && destination && (
                            <>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={dryRunRequested}
                                            disabled={waiting}
                                            onChange={(event) =>
                                                setDryRun(event.target.checked)
                                            }
                                        />
                                    }
                                    label="Test only, do not actually move anything"
                                />
                                <LoadingButton
                                    loading={waiting}
                                    disabled={!canMerge}
                                    startIcon={<CallMergeIcon />}
                                    color={
                                        dryRunRequested ? 'inherit' : 'primary'
                                    }
                                    variant="contained"
                                    onClick={submit}
                                >
                                    {mergeButtonLabel(blocked, dryRunRequested)}
                                </LoadingButton>
                            </>
                        )}
                    </Stack>
                </Paper>
            </Box>
            <SnackbarProvider
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            />
        </DashboardLayout>
    )
}

interface UserSelectProps {
    label: string
    value: AutocompleteOption | null
    onChange: (option: AutocompleteOption | null) => void
    disabled?: boolean
}

function UserSelect({ label, value, onChange, disabled }: UserSelectProps) {
    const [inputValue, setInputValue] = useState('')

    const trimmed = inputValue.trim()
    // An empty term short-circuits the hook, so anything shorter than the
    // minimum never reaches the server.
    const searchTerm = trimmed.length >= MIN_QUERY_LENGTH ? trimmed : ''

    const { results, loading, error } = useDebouncedSearch(
        searchTerm,
        searchUsers
    )

    // The selected option won't be in the current result set once the search
    // term moves on, and MUI warns (and drops the selection) when it can't find
    // the value among the options.
    const options = useMemo(() => {
        if (!value) {
            return results
        }
        return results.some((option) => option.id === value.id)
            ? results
            : [value, ...results]
    }, [results, value])

    const capped = results.length >= SEARCH_LIMIT

    function helperText(): string | undefined {
        if (error) {
            return error
        }
        if (capped) {
            return `Showing the first ${SEARCH_LIMIT} matches. Keep typing to narrow.`
        }
        return undefined
    }

    function noOptionsText(): string {
        if (trimmed.length < MIN_QUERY_LENGTH) {
            return `Type at least ${MIN_QUERY_LENGTH} characters to search`
        }
        return 'No users found'
    }

    return (
        <Autocomplete
            options={options}
            value={value ?? null}
            onChange={(_event, option) => onChange(option)}
            inputValue={inputValue}
            onInputChange={(_event, next) => setInputValue(next)}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, selected) =>
                option.id === selected.id
            }
            // The server already matched on name OR email; re-filtering here
            // would drop every result that matched on email alone.
            filterOptions={(unfiltered) => unfiltered}
            loading={loading}
            noOptionsText={noOptionsText()}
            disabled={disabled}
            disablePortal
            sx={{ minWidth: '20em' }}
            renderOption={(props, option) => (
                <li {...props} key={option.id}>
                    <Stack justifyContent="left" textAlign="left">
                        <Typography variant="body1">
                            {option.label}
                            {option.deleted ? ' (deleted)' : ''}
                        </Typography>
                        <Typography
                            variant="subtitle1"
                            component="p"
                            fontSize="x-small"
                        >
                            {option.email}
                        </Typography>
                    </Stack>
                </li>
            )}
            renderInput={(params) => (
                <TextField
                    {...params}
                    required
                    label={label}
                    error={Boolean(error)}
                    helperText={helperText()}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading ? (
                                    <CircularProgress size={20} />
                                ) : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    )
}
